import { createAction, createAsyncAction } from "@shared/store/helpers";
import {
  CaseListFilter,
  CreateEditResponse,
  PaginatedResponse,
  ResponseError,
  WithCallback,
  WithLoaderFlag,
} from "@shared/interfaces";
import { CaseDocumentModel, CaseModel, CaseStatusModel, MedicalFieldResultModel } from "@shared/models";

import { CasesActionTypes } from "./constants";
import {
  CaseDocumentDto,
  CaseFileDto,
  CheckedCaseFile,
  ChooseMedicalFieldValuePayload,
  VerifyMedicalFieldPayload,
  CreateCaseDto,
  ParseCaseDocumentDto,
  RemoveCaseDocumentPayload,
  UploadCaseFileDto,
} from "../interface";
import { CasesReportStatusDtoInterface } from "../interface/CasesReportStatusDto.interface";

export const getCasesList = createAsyncAction(
  CasesActionTypes.GET_CASES_LIST,
  CasesActionTypes.GET_CASES_LIST_SUCCESS,
  CasesActionTypes.GET_CASES_LIST_FAILURE,
)<WithLoaderFlag<CaseListFilter>, PaginatedResponse<CaseModel>, ResponseError>();

export const getCasesStatuses = createAsyncAction(
  CasesActionTypes.GET_CASES_STATUSES,
  CasesActionTypes.GET_CASES_STATUSES_SUCCESS,
  CasesActionTypes.GET_CASES_STATUSES_FAILURE,
)<number[], CaseStatusModel[], ResponseError>();

export const createCase = createAsyncAction(
  CasesActionTypes.CREATE_CASE,
  CasesActionTypes.CREATE_CASE_SUCCESS,
  CasesActionTypes.CREATE_CASE_FAILURE,
)<WithCallback<CreateCaseDto, CaseModel>, CreateEditResponse<CaseModel>, ResponseError>();

export const deleteCase = createAsyncAction(
  CasesActionTypes.DELETE_CASE,
  CasesActionTypes.DELETE_CASE_SUCCESS,
  CasesActionTypes.DELETE_CASE_FAILURE,
)<WithCallback<{ caseId: number }, undefined>, undefined, ResponseError>();

export const getCase = createAsyncAction(
  CasesActionTypes.GET_CASE,
  CasesActionTypes.GET_CASE_SUCCESS,
  CasesActionTypes.GET_CASE_FAILURE,
)<WithLoaderFlag<{ caseId: number }>, CaseModel | null, ResponseError>();

export const getCaseData = createAsyncAction(
  CasesActionTypes.GET_CASE_DATA,
  CasesActionTypes.GET_CASE_DATA_SUCCESS,
  CasesActionTypes.GET_CASE_DATA_FAILURE,
)<WithCallback<{ caseId: number }, CaseModel>, CaseModel, ResponseError>();

export const restartProcessingCase = createAsyncAction(
  CasesActionTypes.RESTART_PROCESSING_CASE,
  CasesActionTypes.RESTART_PROCESSING_CASE_SUCCESS,
  CasesActionTypes.RESTART_PROCESSING_CASE_FAILURE,
)<number, CreateEditResponse<CaseModel>, ResponseError>();

export const updateCaseReportStatus = createAsyncAction(
  CasesActionTypes.UPDATE_CASE_REPORT_STATUS,
  CasesActionTypes.UPDATE_CASE_REPORT_STATUS_SUCCESS,
  CasesActionTypes.UPDATE_CASE_REPORT_STATUS_FAILURE,
)<
  WithCallback<CasesReportStatusDtoInterface, CasesReportStatusDtoInterface>,
  CasesReportStatusDtoInterface,
  ResponseError
>();

export const getCaseFileSignedUrl = createAsyncAction(
  CasesActionTypes.GET_CASE_FILE_SIGNED_URL,
  CasesActionTypes.GET_CASE_FILE_SIGNED_URL_SUCCESS,
  CasesActionTypes.GET_CASE_FILE_SIGNED_URL_FAILURE,
)<number, number, ResponseError>();

export const getCaseFileViewUrl = createAsyncAction(
  CasesActionTypes.GET_CASE_FILE_VIEW_URL,
  CasesActionTypes.GET_CASE_FILE_VIEW_URL_SUCCESS,
  CasesActionTypes.GET_CASE_FILE_VIEW_URL_FAILURE,
)<WithCallback<CaseFileDto, string>, undefined, ResponseError>();

export const getCaseDocuments = createAsyncAction(
  CasesActionTypes.GET_CASE_DOCUMENTS,
  CasesActionTypes.GET_CASE_DOCUMENTS_SUCCESS,
  CasesActionTypes.GET_CASE_DOCUMENTS_FAILURE,
)<number, CaseDocumentModel[], ResponseError>();

// Upload problems are shown on the file's row, so there are no success and failure actions to toast them.
export const uploadCaseFile = createAction(CasesActionTypes.UPLOAD_CASE_FILE)<
  WithCallback<UploadCaseFileDto, CheckedCaseFile>
>();

export const deleteCaseFile = createAsyncAction(
  CasesActionTypes.DELETE_CASE_FILE,
  CasesActionTypes.DELETE_CASE_FILE_SUCCESS,
  CasesActionTypes.DELETE_CASE_FILE_FAILURE,
)<WithCallback<CaseFileDto>, undefined, ResponseError>();

export const discardTemporaryCaseFiles = createAction(CasesActionTypes.DISCARD_TEMPORARY_CASE_FILES)<CaseDocumentDto>();

export const parseCaseDocument = createAsyncAction(
  CasesActionTypes.PARSE_CASE_DOCUMENT,
  CasesActionTypes.PARSE_CASE_DOCUMENT_SUCCESS,
  CasesActionTypes.PARSE_CASE_DOCUMENT_FAILURE,
)<WithCallback<ParseCaseDocumentDto>, CaseDocumentModel[], ResponseError>();

export const retryCaseDocument = createAsyncAction(
  CasesActionTypes.RETRY_CASE_DOCUMENT,
  CasesActionTypes.RETRY_CASE_DOCUMENT_SUCCESS,
  CasesActionTypes.RETRY_CASE_DOCUMENT_FAILURE,
)<CaseDocumentDto, CaseDocumentModel[], ResponseError>();

export const getMedicalRecordsResult = createAsyncAction(
  CasesActionTypes.GET_MEDICAL_RECORDS_RESULT,
  CasesActionTypes.GET_MEDICAL_RECORDS_RESULT_SUCCESS,
  CasesActionTypes.GET_MEDICAL_RECORDS_RESULT_FAILURE,
)<number, MedicalFieldResultModel[], ResponseError>();

// A failed removal is shown in its window, so there is no failure action to toast it.
export const removeCaseDocument = createAction(CasesActionTypes.REMOVE_CASE_DOCUMENT)<RemoveCaseDocumentPayload>();

export const removeCaseDocumentSuccess = createAction(CasesActionTypes.REMOVE_CASE_DOCUMENT_SUCCESS)<CaseModel>();

// A failed choice is shown in its window, so there is no failure action to toast it.
export const chooseMedicalFieldValue = createAction(
  CasesActionTypes.CHOOSE_MEDICAL_FIELD_VALUE,
)<ChooseMedicalFieldValuePayload>();

export const chooseMedicalFieldValueSuccess = createAction(
  CasesActionTypes.CHOOSE_MEDICAL_FIELD_VALUE_SUCCESS,
)<CaseModel>();

// A failed verification is shown in the field's note, so there is no failure action to toast it.
export const verifyMedicalField = createAction(CasesActionTypes.VERIFY_MEDICAL_FIELD)<VerifyMedicalFieldPayload>();

export const verifyMedicalFieldSuccess = createAction(CasesActionTypes.VERIFY_MEDICAL_FIELD_SUCCESS)<CaseModel>();

/** Takes the fields parsed documents save to, with their sources and messages, from a refetched case. */
export const syncCaseDocumentFields = createAction(CasesActionTypes.SYNC_CASE_DOCUMENT_FIELDS)<CaseModel>();

export const payForCase = createAsyncAction(
  CasesActionTypes.PAY_FOR_CASE,
  CasesActionTypes.PAY_FOR_CASE_SUCCESS,
  CasesActionTypes.PAY_FOR_CASE_FAILURE,
)<number, undefined, ResponseError>();

export const updateCase = createAsyncAction(
  CasesActionTypes.UPDATE_CASE,
  CasesActionTypes.UPDATE_CASE_SUCCESS,
  CasesActionTypes.UPDATE_CASE_FAILURE,
)<WithCallback<Partial<CaseModel>, CaseModel>, CreateEditResponse<CaseModel>, ResponseError>();

export const setFilter = createAction(CasesActionTypes.SET_FILTER)<CaseListFilter | null>();
export const clearCasesList = createAction(CasesActionTypes.CLEAR_CASES_LIST)();
export const setProcessingCaseId = createAction(CasesActionTypes.SET_PROCESSING_CASE_ID)<number | undefined>();
